import { useState, useEffect } from 'react';
import { CompanyDomainsAndAssessment } from './CompanyDomainsAndAssessment';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  AlertCircle, 
  CheckCircle, 
  Building, 
  Users, 
  FileText, 
  Settings,
  Trash2,
  Plus,
  Clock,
  Shield,
  XCircle,
  AlertTriangle,
  Edit,
  Check,
  Ban,
  Building2,
  Crown,
  Mail,
  Target,
  UsersIcon
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { SOWList } from './SOWList';
import { SOWDialog } from './SOWDialog';
import { UserEditDialog } from './UserEditDialog';
import { AuditLog } from './AuditLog';
import { PurgeCompanyDataDialog } from './PurgeCompanyDataDialog';
import { CompanyConfigurationGuide } from './CompanyConfigurationGuide';
import { ForgeIntegrationSettings } from './ForgeIntegrationSettings';
import { TemplateSelector } from './templates/TemplateSelector';
import { TemplateIcon } from './templates';
import { hasRole, getPrimaryRole, getRoleDisplayName } from '../utils/roleUtils';

interface User {
  id: string;
  email: string;
  team: string | null;
  role: string;
  isCompanyAdmin: boolean;
  status: string;
  createdAt: string;
  companyId: string;
}

interface Company {
  id: string;
  name: string;
  createdAt: string;
  recovered?: boolean;
}

interface CompanyEditDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  accessToken: string;
  onUpdated: () => void;
  sows?: SOW[];
  onAddSOW?: () => void;
  onEditSOW?: (sowId: string) => void;
  onDeleteSOW?: (sowId: string) => void;
  isConsultant?: boolean;
  isSystemAdmin?: boolean;
  projects?: Project[];
  currentUser?: any;
}

export function CompanyEditDialog({ 
  open, 
  onClose, 
  companyId, 
  accessToken, 
  onUpdated,
  sows: externalSOWs,
  onAddSOW: externalOnAddSOW,
  currentUser,
  onEditSOW: externalOnEditSOW,
  onDeleteSOW: externalOnDeleteSOW,
  isConsultant = false,
  isSystemAdmin = false,
  projects = []
}: CompanyEditDialogProps) {
  const [company, setCompany] = useState<Company | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const [sows, setSOWs] = useState<SOW[]>([]);
  const [sowDialogOpen, setSOWDialogOpen] = useState(false);
  const [editingSOW, setEditingSOW] = useState<SOW | undefined>(undefined);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [userEditDialogOpen, setUserEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [togglingStatus, setTogglingStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('company');
  const [refreshKey, setRefreshKey] = useState(0);
  const [companyTemplate, setCompanyTemplate] = useState<TemplateType | null>(null);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('custom');

  useEffect(() => {
    if (open) {
      console.log('=== CompanyEditDialog useEffect ===');
      console.log('open:', open);
      console.log('companyId:', companyId);
      console.log('accessToken exists:', !!accessToken);
      
      if (!companyId) {
        console.error('CompanyEditDialog: No companyId provided!');
        setError('No company ID provided');
        return;
      }
      
      if (!accessToken) {
        console.error('CompanyEditDialog: No accessToken provided!');
        setError('No access token provided');
        return;
      }
      
      loadCompanyDetails();
      loadCompanySOWs();
    } else {
      // Reset state when dialog closes
      setCompany(null);
      setUsers([]);
      setCompanyName('');
      setError('');
      setSuccess('');
      setSOWs([]);
    }
  }, [open, companyId, accessToken]);

  // Refresh configuration data when switching to the configuration tab
  useEffect(() => {
    if (activeTab === 'configuration' && open) {
      setRefreshKey(prev => prev + 1);
    }
  }, [activeTab, open]);

  const loadCompanyDetails = async () => {
    setLoading(true);
    setError('');
    
    try {
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/companies/${companyId}`;
      console.log('=== CompanyEditDialog: Loading company details ===');
      console.log('Company ID:', companyId);
      console.log('Full URL:', url);
      console.log('Access token (first 20 chars):', accessToken?.substring(0, 20) + '...');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response data:', errorData);
        
        if (response.status === 404) {
          const debugInfo = errorData.debug ? JSON.stringify(errorData.debug, null, 2) : '';
          console.error('404 Debug info:', errorData.debug);
          throw new Error(
            `Company not found in database. ${errorData.error || ''}\n\nDebug info: ${debugInfo}\n\nThe company may have been deleted or the data is corrupted. Please contact support.`
          );
        }
        
        throw new Error(errorData.error || `Failed to load company details (Status: ${response.status})`);
      }

      const data = await response.json();
      console.log('Successfully loaded company data:', data);
      setCompany(data.company);
      setUsers(data.users);
      setCompanyName(data.company.name);
      
      // Show info message if company was auto-recovered
      if (data.company.recovered) {
        setSuccess('Note: This company record was automatically recovered from user data.');
      }

      // Load company template
      const templateResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/template`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (templateResponse.ok) {
        const templateData = await templateResponse.json();
        setCompanyTemplate(templateData.template);
        setSelectedTemplate(templateData.template);
      }
    } catch (err: any) {
      console.error('Error loading company details:', err);
      setError(err.message || 'Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCompanyName = async () => {
    if (!companyName.trim()) {
      setError('Company name cannot be empty');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/update-company`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            companyId,
            companyName: companyName.trim(),
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update company');
      }

      setSuccess('Company name updated successfully');
      onUpdated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating company:', err);
      setError(err.message || 'Failed to update company');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (userId: string) => {
    setResendingInvite(userId);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/resend-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to resend invitation');
      }

      setSuccess('Invitation email sent successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error resending invite:', err);
      setError(err.message || 'Failed to resend invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  const loadCompanySOWs = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSOWs(data.sows || []);
      }
    } catch (err: any) {
      console.error('Error loading SOWs:', err);
    }
  };

  const saveCompanySOWs = async (updatedSOWs: SOW[]) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ 
            sows: updatedSOWs, 
            companyId 
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save SOWs');
      }
    } catch (err: any) {
      console.error('Error saving SOWs:', err);
      throw err;
    }
  };

  const getRoleBadge = (user: User) => {
    if (user.role === 'systemAdmin') {
      return <Badge variant="default">System Admin</Badge>;
    }
    if (user.role === 'cfAdmin') {
      return <Badge variant="secondary" className="bg-blue-600 text-white">Firm Admin</Badge>;
    }
    if (user.role === 'companyAdmin' || user.isCompanyAdmin) {
      return <Badge variant="secondary">Client Engagement Lead</Badge>;
    }
    if (user.role === 'consultant') {
      return <Badge variant="outline" className="border-blue-500 text-blue-700">Consultant</Badge>;
    }
    return <Badge variant="outline">User</Badge>;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
      case 'active':
        return (
          <Badge variant="default" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Active
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary" className="gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      case 'invited':
        return (
          <Badge variant="outline" className="gap-1">
            <Mail className="h-3 w-3" />
            Invited
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Rejected
          </Badge>
        );
      case 'disabled':
        return (
          <Badge variant="outline" className="gap-1 border-gray-400 text-gray-600">
            <Ban className="h-3 w-3" />
            Disabled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const companyAdmins = users.filter(u => u.isCompanyAdmin || u.role === 'companyAdmin');

  const handleAddSOW = async (sow: Omit<SOW, 'id'>) => {
    try {
      let updatedSOWs: SOW[];
      if (editingSOW) {
        updatedSOWs = sows.map(s =>
          s.id === editingSOW.id ? { ...sow, id: editingSOW.id } : s
        );
      } else {
        const newSOW: SOW = {
          ...sow,
          id: Date.now().toString()
        };
        updatedSOWs = [...sows, newSOW];
      }
      
      await saveCompanySOWs(updatedSOWs);
      setSOWs(updatedSOWs);
      setSOWDialogOpen(false);
      setEditingSOW(undefined);
      setSuccess('SOW saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save SOW');
    }
  };

  const handleEditSOW = (sowId: string) => {
    const sow = sows.find(s => s.id === sowId);
    if (sow) {
      setEditingSOW(sow);
      setSOWDialogOpen(true);
    }
  };

  const handleDeleteSOW = async (sowId: string) => {
    if (confirm('Are you sure you want to delete this SOW? This will also delete all its change orders.')) {
      try {
        const updatedSOWs = sows.filter(s => s.id !== sowId);
        await saveCompanySOWs(updatedSOWs);
        setSOWs(updatedSOWs);
        setSuccess('SOW deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to delete SOW');
      }
    }
  };

  const handleOpenAddSOW = () => {
    setEditingSOW(undefined);
    setSOWDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserEditDialogOpen(true);
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    const action = currentStatus === 'disabled' ? 'enable' : 'disable';
    
    if (!confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    setTogglingStatus(userId);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/toggle-user-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to toggle user status');
      }

      setSuccess(`User ${action}d successfully`);
      loadCompanyDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      setError(err.message || 'Failed to toggle user status');
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!companyTemplate || selectedTemplate === companyTemplate) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/template`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ template: selectedTemplate }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update template');
      }

      setCompanyTemplate(selectedTemplate);
      setEditingTemplate(false);
      setSuccess('Company template updated successfully');
      onUpdated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating template:', err);
      setError(err.message || 'Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[90vw] md:max-w-[90vw] lg:max-w-[1400px] xl:max-w-[1400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Company Profile</DialogTitle>
          <DialogDescription>
            Manage company information, users, and SOWs (ID: {companyId || 'none'})
          </DialogDescription>
        </DialogHeader>

        {loading && !company && (
          <div className="py-8 text-center text-muted-foreground">
            Loading company details for ID: {companyId}...
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {company && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="company">
                <Building2 className="h-4 w-4 mr-2" />
                Company Info
              </TabsTrigger>
              <TabsTrigger value="users">
                <UsersIcon className="h-4 w-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="configuration">
                <Target className="h-4 w-4 mr-2" />
                Configuration
              </TabsTrigger>
              <TabsTrigger value="sows">
                <FileText className="h-4 w-4 mr-2" />
                SOWs & Change Orders
              </TabsTrigger>
              <TabsTrigger value="audit">
                <FileText className="h-4 w-4 mr-2" />
                Audit Log
              </TabsTrigger>
              <TabsTrigger value="setup">
                <Target className="h-4 w-4 mr-2" />
                Domains & assessment
              </TabsTrigger>
            </TabsList>

            <TabsContent value="company" className="space-y-6 mt-6">
              {/* Company Name Section */}
              <div className="space-y-4">
                <div>
                  <h3>Company Information</h3>
                  <p className="text-muted-foreground">
                    Update company name and basic details
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="company-name">Company Name</Label>
                    <Input
                      id="company-name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleUpdateCompanyName}
                      disabled={loading || companyName === company.name}
                    >
                      Update Name
                    </Button>
                  </div>
                </div>

                <div className="text-muted-foreground">
                  Created: {new Date(company.createdAt).toLocaleDateString()}
                  {company.recovered && (
                    <Badge variant="outline" className="ml-2 border-yellow-500 text-yellow-700">
                      Auto-recovered
                    </Badge>
                  )}
                </div>
              </div>

              {/* ITSM Platform Template Section */}
              {companyTemplate && (isSystemAdmin || isConsultant) && (
                <div className="space-y-4">
                  <div>
                    <h3>ITSM Platform Template</h3>
                    <p className="text-muted-foreground">
                      The platform template determines available features and export formats
                    </p>
                  </div>

                  {!editingTemplate ? (
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <TemplateIcon template={companyTemplate} className="h-6 w-6" />
                        <div>
                          <p className="font-medium">{companyTemplate.charAt(0).toUpperCase() + companyTemplate.slice(1)} Template</p>
                          <p className="text-sm text-muted-foreground">Current platform configuration</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => setEditingTemplate(true)}
                      >
                        Change Template
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 p-4 border rounded-lg">
                      <TemplateSelector 
                        selectedTemplate={selectedTemplate}
                        onSelect={setSelectedTemplate}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={handleUpdateTemplate}
                          disabled={loading || selectedTemplate === companyTemplate}
                        >
                          Save Template
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedTemplate(companyTemplate);
                            setEditingTemplate(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Changing the template will update available features and export formats for this company.
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>
              )}

              {/* Company Admins Section */}
              <div className="space-y-4">
                <div>
                  <h3>Client Engagement Leads</h3>
                  <p className="text-muted-foreground">
                    Users with admin privileges for this company
                  </p>
                </div>

                {companyAdmins.length > 0 ? (
                  <div className="space-y-2">
                    {companyAdmins.map((admin) => (
                      <div key={admin.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Crown className="h-4 w-4 text-yellow-600" />
                          <div>
                            <p>{admin.email}</p>
                            {admin.team && (
                              <p className="text-muted-foreground">Team: {admin.team}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getRoleBadge(admin)}
                          {getStatusBadge(admin.status)}
                          {admin.status === 'invited' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendInvite(admin.id)}
                              disabled={resendingInvite === admin.id}
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              {resendingInvite === admin.id ? 'Sending...' : 'Resend Invite'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No company administrators assigned</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="users" className="space-y-6 mt-6">
              {/* All Users Section */}
              <div className="space-y-4">
                <div>
                  <h3>All Users ({users.length})</h3>
                  <p className="text-muted-foreground">
                    Complete list of users in this company
                  </p>
                </div>

                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow 
                          key={user.id}
                          className={`cursor-pointer hover:bg-muted/50 ${user.status === 'disabled' ? 'opacity-60' : ''}`}
                          onClick={() => isSystemAdmin && handleEditUser(user)}
                        >
                          <TableCell className={user.status === 'disabled' ? 'text-muted-foreground' : ''}>
                            {user.email}
                          </TableCell>
                          <TableCell className={user.status === 'disabled' ? 'text-muted-foreground' : ''}>
                            {user.team || '—'}
                          </TableCell>
                          <TableCell>{getRoleBadge(user)}</TableCell>
                          <TableCell>{getStatusBadge(user.status)}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              {user.status === 'invited' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResendInvite(user.id)}
                                  disabled={resendingInvite === user.id}
                                >
                                  <Mail className="h-4 w-4 mr-1" />
                                  {resendingInvite === user.id ? 'Sending...' : 'Resend'}
                                </Button>
                              )}
                              {isSystemAdmin && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditUser(user)}
                                  >
                                    <Edit className="h-4 w-4 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={user.status === 'disabled' ? 'default' : 'outline'}
                                    onClick={() => handleToggleUserStatus(user.id, user.status)}
                                    disabled={togglingStatus === user.id}
                                  >
                                    {user.status === 'disabled' ? (
                                      <>
                                        <Check className="h-4 w-4 mr-1" />
                                        Enable
                                      </>
                                    ) : (
                                      <>
                                        <Ban className="h-4 w-4 mr-1" />
                                        Disable
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="configuration" className="space-y-6 mt-6">
              <CompanyConfigurationGuide
                companyId={companyId}
                companyName={company.name}
                projects={projects}
                totalUsers={users.length}
                accessToken={accessToken}
                currentUser={currentUser || { id: '', email: '', name: '' }}
                refreshKey={refreshKey}
              />
              {/* Forge Integration - Only for Atlassian template */}
              {companyTemplate === 'atlassian' && (
                <ForgeIntegrationSettings
                  accessToken={accessToken}
                  companyId={companyId}
                  companyName={company.name}
                />
              )}
            </TabsContent>

            <TabsContent value="sows" className="space-y-6 mt-6">
              <SOWList
                sows={sows}
                onAddSOW={handleOpenAddSOW}
                onEditSOW={handleEditSOW}
                onDeleteSOW={handleDeleteSOW}
                isConsultant={isConsultant}
                isSystemAdmin={isSystemAdmin}
              />
            </TabsContent>

            <TabsContent value="audit" className="space-y-6 mt-6">
              <AuditLog 
                accessToken={accessToken} 
                companyId={companyId}
                isSystemAdmin={isSystemAdmin}
              />
            </TabsContent>

            {/* Subdomain, custom domain, the JSM assessment and action items (the retired CompanyDashboard, RESTORE_PLAN R3.6) */}
            <TabsContent value="setup" className="space-y-6 mt-6">
              {company && (
                <CompanyDomainsAndAssessment companyId={companyId} company={company} accessToken={accessToken} currentUser={currentUser} onUpdated={onUpdated} />
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <div className="flex justify-between items-center w-full">
            <div>
              {(isSystemAdmin || isConsultant) && (
                <Button 
                  variant="destructive" 
                  onClick={() => setPurgeDialogOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Purge Company Data
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {/* SOW Dialog */}
      <SOWDialog
        open={sowDialogOpen}
        onClose={() => {
          setSOWDialogOpen(false);
          setEditingSOW(undefined);
        }}
        onSave={handleAddSOW}
        editSOW={editingSOW}
      />

      {/* Purge Data Dialog */}
      {company && (
        <PurgeCompanyDataDialog
          open={purgeDialogOpen}
          onClose={() => setPurgeDialogOpen(false)}
          onSuccess={(wasCompanyDeleted?: boolean) => {
            setPurgeDialogOpen(false);
            if (wasCompanyDeleted) {
              // Company was deleted (either complete deletion or selective with company record checked)
              setSuccess('Company deleted successfully');
              onClose(); // Close the CompanyEditDialog
              onUpdated(); // Refresh companies list
            } else {
              // Partial purge - reload company details
              setSuccess('Company data purged successfully');
              loadCompanyDetails();
              onUpdated();
            }
          }}
          companyId={companyId}
          companyName={company.name}
          accessToken={accessToken}
        />
      )}

      {/* User Edit Dialog */}
      <UserEditDialog
        open={userEditDialogOpen}
        onClose={() => {
          setUserEditDialogOpen(false);
          setEditingUser(null);
        }}
        user={editingUser}
        accessToken={accessToken}
        onUpdated={() => {
          loadCompanyDetails();
          onUpdated();
        }}
        isSystemAdmin={isSystemAdmin}
        isCompanyAdmin={!isSystemAdmin && !isConsultant}
        currentUserId={currentUser?.id}
        currentUserCompanyId={currentUser?.companyId}
      />
    </Dialog>
  );
}